import React, { useEffect, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import Spinner from "../../../components/spinner";
import MuiAlert from "@mui/material/Alert";
import { GrAdd } from "react-icons/gr";
import { useNavigate, useParams } from "react-router-dom";
import useCreate from "../../../hooks/useCreate";
import useGet from "../../../hooks/useGet";
import useUploadMedia from "../../../hooks/file/useUploadMedia";
import Loader from "../../../components/loader";
import toast from "react-hot-toast";
import Editor from "../../../components/elements/Editor";
import { set, useForm, useFieldArray } from "react-hook-form";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function NewEditProduct() {
  const navigate = useNavigate();
  const { id } = useParams();
  const fileInputRef = useRef(null);
  const logoFileInputRef = useRef(null); // Ref for logo file input
  // const secondaryImageFileInputRef = useRef(null); // Ref for secondary image file input
  const thumbnailFileInputRef = useRef(null); // Ref for thumbnail file input

  // States
  const [imagePreview, setImagePreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null); // Preview for logo
  // const [secondaryImagePreview, setSecondaryImagePreview] = useState(null); // Preview for secondary image
  const [thumbnailPreview, setThumbnailPreview] = useState(null); // Preview for thumbnail
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Initialize react-hook-form
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "product_links"
  });

  // Get product details
  const { data, isLoading: isLoadingProduct } = useGet({
    url: "get-product-data",
    initialParams: { product_id: id },
    onSuccess: (response) => {
      // If there's product data, populate the form
      if (response && response.Data) {
        const product = response.Data;

        // Set form values
        setValue("product_id", product.product_id);
        setValue("name", product.name);
        setValue("title", product.title);
        setValue("slug", product.slug);
        setValue("overview", product.overview);
        setValue("video", product.video || "");
        setValue("banner", product.banner);
        setValue("logo", product.logo);
        // setValue("secondary_img", product.secondary_img);
        setValue("thumbnail", product.thumbnail);
        setValue("client", product.client || "");
        setValue("genre", product.genre || "");
        setValue("services", product.services || "");
        setValue("platform", product.platform || "");
        // setValue("links", product.links || "");
        setValue("product_links", (product.product_links && product.product_links.length > 0) ? product.product_links : [{ name: "", link: "" }]);

        // Set banner preview if exists
        if (product.banner) {
          setImagePreview(product.banner);
        }
        // Set logo preview if exists
        if (product.logo) {
          setLogoPreview(product.logo);
        }
        // if (product.secondary_img) {
        //   setSecondaryImagePreview(product.secondary_img);
        // }
        if (product.thumbnail) {
          setThumbnailPreview(product.thumbnail);
        }
      }
    },
    onError: (err) => {
      toast.error(`Error loading product: ${err?.message || 'Unknown error'}`, {
        position: "top-right",
        duration: 3000,
      });
      setIsError(true);
    },
  });

  // Register fields that need custom handling
  useEffect(() => {
    register("overview", { required: "Overview is required" });
    register("banner"); // Make banner optional
    register("logo"); // Register logo field
    // register("secondary_img");
    register("thumbnail");
  }, [register]);

  // Watch fields for validation
  const watchedOverview = watch("overview");
  const watchedBanner = watch("banner");
  const watchedLogo = watch("logo"); // Watch logo for validation
  // const watchedSecondaryImage = watch("secondary_img");
  const watchedThumbnail = watch("thumbnail"); // Watch thumbnail for validation
  const watchedTitle = watch("title"); // Watch title for slug generation

  // Add generate slug function
  const generateSlug = () => {
    if (watchedTitle) {
      const slug = watchedTitle
        .toLowerCase()
        .replace(/[^\w\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-'); // Replace multiple hyphens with single hyphen

      setValue("slug", slug);
    }
  };

  // Handle editor changes
  const handleChangeEditor = (newValue) => {
    setValue("overview", newValue, { shouldValidate: true });
  };

  // Update product API
  const { setDataToServer } = useCreate({
    url: "update-product",
    onSuccess: () => {
      toast.success("Product Updated Successfully", {
        position: "top-right",
        duration: 3000,
      });
      setIsSuccess(true);
      setIsError(false);
      navigate("/catalog/products");
    },
    onError: (err) => {
      setIsError(true);
      setIsSuccess(false);
      toast.error(`Error: ${err?.message || 'Failed to update product'}`, {
        position: "top-right",
        duration: 3000,
      });
    },
  });

  // Handle banner image upload (single file)
  const { upload } = useUploadMedia({
    url: "upload-document",
    onSuccess: (res) => {
      console.log("Upload response:", res);

      const imagePath = res?.image_path;
      const documentId = res?.document_id;

      if (imagePath) {
        setValue("banner", imagePath, { shouldValidate: true });
        setImagePreview(imagePath);
        toast.success("Banner Image Uploaded Successfully", {
          position: "top-right",
          duration: 3000,
        });
      } else {
        console.error("Failed to get image path. Response:", res);
        toast.error("Failed to get image path from upload response.", {
          position: "top-right",
          duration: 3000,
        });
        setValue("banner", "", { shouldValidate: true });
      }
    },
    onError: (err) => {
      toast.error(`Image Upload Failed: ${err?.message || 'Unknown error'}`, {
        position: "top-right",
        duration: 3000,
      });
      setValue("banner", "", { shouldValidate: true });
    }
  });

  // Logo upload handler (same as banner)
  const { upload: uploadLogo } = useUploadMedia({
    url: "upload-document",
    onSuccess: (res) => {
      const imagePath = res?.image_path;
      if (imagePath) {
        setValue("logo", imagePath, { shouldValidate: true });
        setLogoPreview(imagePath);
        toast.success("Logo Uploaded Successfully", {
          position: "top-right",
          duration: 3000,
        });
      } else {
        toast.error("Failed to get logo path from upload response.", {
          position: "top-right",
          duration: 3000,
        });
        setValue("logo", "", { shouldValidate: true });
      }
    },
    onError: (err) => {
      toast.error(`Logo Upload Failed: ${err?.message || 'Unknown error'}`, {
        position: "top-right",
        duration: 3000,
      });
      setValue("logo", "", { shouldValidate: true });
    }
  });

  // Thumbnail upload handler
  const { upload: uploadThumbnail } = useUploadMedia({
    url: "upload-document",
    onSuccess: (res) => {
      const imagePath = res?.image_path;
      if (imagePath) {
        setValue("thumbnail", imagePath, { shouldValidate: true });
        setThumbnailPreview(imagePath);
        toast.success("Thumbnail Uploaded Successfully", {
          position: "top-right",
          duration: 3000,
        });
      } else {
        toast.error("Failed to get Thumbnail path from upload response.", {
          position: "top-right",
          duration: 3000,
        });
        setValue("thumbnail", "", { shouldValidate: true });
      }
    },
    onError: (err) => {
      toast.error(`Thumbnail Upload Failed: ${err?.message || 'Unknown error'}`, {
        position: "top-right",
        duration: 3000,
      });
      setValue("thumbnail", "", { shouldValidate: true });
    }
  });

  // const { upload: uploadSecondaryImage } = useUploadMedia({
  //   url: "upload-document",
  //   onSuccess: (res) => {
  //     const imagePath = res?.image_path;
  //     if (imagePath) {
  //       setValue("secondary_img", imagePath, { shouldValidate: true });
  //       setSecondaryImagePreview(imagePath);
  //       toast.success("Secondary Image Uploaded Successfully", {
  //         position: "top-right",
  //         duration: 3000,
  //       });
  //     } else {
  //       toast.error("Failed to get Secondary Image path from upload response.", {
  //         position: "top-right",
  //         duration: 3000,
  //       });
  //       setValue("secondary_img", "", { shouldValidate: true });
  //     }
  //   },
  //   onError: (err) => {
  //     toast.error(`Secondary Image Upload Failed: ${err?.message || 'Unknown error'}`, {
  //       position: "top-right",
  //       duration: 3000,
  //     });
  //     setValue("secondary_img", "", { shouldValidate: true });
  //   }
  // });

  //   const { upload: uploadThumbnail } = useUploadMedia({
  //   url: "upload-document",
  //   onSuccess: (res) => {
  //     const imagePath = res?.image_path;
  //     if (imagePath) {
  //       setValue("thumbnail", imagePath, { shouldValidate: true });
  //       setThumbnailPreview(imagePath);
  //       toast.success("Thumbnail Uploaded Successfully", {
  //         position: "top-right",
  //         duration: 3000,
  //       });
  //     } else {
  //       toast.error("Failed to get Thumbnail path from upload response.", {
  //         position: "top-right",
  //         duration: 3000,
  //       });
  //       setValue("thumbnail", "", { shouldValidate: true });
  //     }
  //   },
  //   onError: (err) => {
  //     toast.error(`Thumbnail Upload Failed: ${err?.message || 'Unknown error'}`, {
  //       position: "top-right",
  //       duration: 3000,
  //     });
  //     setValue("thumbnail", "", { shouldValidate: true });
  //   }
  // });

  // Handle form submission
  const onFormSubmit = (formData) => {
    // Filter out empty product links
    if (formData.product_links) {
      formData.product_links = formData.product_links.filter(
        link => link.name.trim() !== "" || link.link.trim() !== ""
      );
    }
    setDataToServer.mutate(formData);
  };

  // Handle banner file upload
  const handleUploadFile = (e) => {
    const bannerFile = e.target?.files[0];
    if (!bannerFile) return;

    // Basic validation
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];

    if (!allowedTypes.includes(bannerFile.type)) {
      toast.error("Invalid file type. Please upload JPG, JPEG, or PNG.", { duration: 3000 });
      e.target.value = null;
      return;
    }

    const data = {
      file: bannerFile,
      type: 'products'
    };
    upload.mutate(data);
    e.target.value = null;
  };

  // Thumbnail file upload
  const handleUploadLogoFile = (e) => {
    const logoFile = e.target?.files[0];
    if (!logoFile) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(logoFile.type)) {
      toast.error("Invalid file type. Please upload JPG, JPEG, or PNG.", { duration: 3000 });
      e.target.value = null;
      return;
    }
    const data = {
      file: logoFile,
      type: 'products'
    };
    uploadLogo.mutate(data);
    e.target.value = null;
  };

  // Thumbnail file upload
  const handleUploadThumbnailFile = (e) => {
    const thumbnailFile = e.target?.files[0];
    if (!thumbnailFile) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(thumbnailFile.type)) {
      toast.error("Invalid file type. Please upload JPG, JPEG, or PNG.", { duration: 3000 });
      e.target.value = null;
      return;
    }
    const data = {
      file: thumbnailFile,
      type: 'products'
    };
    uploadThumbnail.mutate(data);
    e.target.value = null;
  };

  const handleDeleteBanner = () => {
    setValue("banner", "", { shouldValidate: true });
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeleteLogo = () => {
    setValue("logo", "", { shouldValidate: true });
    setLogoPreview(null);
    if (logoFileInputRef.current) {
      logoFileInputRef.current.value = "";
    }
  };

  const handleDeleteThumbnail = () => {
    setValue("thumbnail", "", { shouldValidate: true });
    setThumbnailPreview(null);
    if (thumbnailFileInputRef.current) {
      thumbnailFileInputRef.current.value = "";
    }
  };

  // const handleUploadSecondaryImageFile = (e) => {
  //   const secondaryImageFile = e.target?.files[0];
  //   if (!secondaryImageFile) return;
  //   const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
  //   if (!allowedTypes.includes(secondaryImageFile.type)) {
  //     toast.error("Invalid file type. Please upload JPG, JPEG, or PNG.", { duration: 3000 });
  //     e.target.value = null;
  //     return;
  //   }
  //   const data = {
  //     file: secondaryImageFile,
  //     type: 'products'
  //   };
  //   uploadSecondaryImage.mutate(data);
  //   e.target.value = null;
  // };

  // const handleUploadThumbnailFile = (e) => {
  //   const thumbnailFile = e.target?.files[0];
  //   if (!thumbnailFile) return;
  //   const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
  //   if (!allowedTypes.includes(thumbnailFile.type)) {
  //     toast.error("Invalid file type. Please upload JPG, JPEG, or PNG.", { duration: 3000 });
  //     e.target.value = null;
  //     return;
  //   }
  //   const data = {
  //     file: thumbnailFile,
  //     type: 'products'
  //   };
  //   uploadThumbnail.mutate(data);
  //   e.target.value = null;
  // };

  const handleOpenFileUploadDialog = () => {
    fileInputRef.current.click();
  };
  const handleOpenLogoUploadDialog = () => {
    logoFileInputRef.current.click();
  };
  // const handleOpenSecondaryImageUploadDialog = () => {
  //   secondaryImageFileInputRef.current.click();
  // };
  const handleOpenThumbnailUploadDialog = () => {
    thumbnailFileInputRef.current.click();
  };

  const closeErrorMessage = () => {
    setIsError(false);
  };

  useEffect(() => {
    if (isError) setTimeout(() => setIsError(false), 4000);
    if (isSuccess) setTimeout(() => setIsSuccess(false), 4000);
  }, [isError, isSuccess]);

  return (
    <>
      {/* Full-page loader - ONLY for page-level operations */}
      {(upload.isLoading || setDataToServer.isLoading) && (
        <div className="fixed w-full h-full bg-[#ffffff70] top-0 left-0 z-50">
          <div className="flex w-full items-center justify-center h-full">
            <Loader className={"text-black !w-12 !h-12"} />
          </div>
        </div>
      )}
      <div className="container_xxl bg-[#eceff1] px-4">
        <div className="flex items-center space-x-4 headingBorder__b sticky top-0 z-10 bg-[#eceff1]">
          <div>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
          </div>
          <div>
            <h1 className="heading">Edit Project</h1>
          </div>
        </div>

        {isLoadingProduct ? (
          <div className="py-8 flex justify-center">
            <Loader />
          </div>
        ) : (
          <div className="form-body py-8">
            <form className="" autoComplete="off" onSubmit={handleSubmit(onFormSubmit)}>
              <div className="grid grid-cols-1 gap-4">
                <div className="mb-3 flex items-center justify-between border-b border-gray-400">
                  <h1 className="font-semibold text-2xl pb-3">Project Information</h1>
                </div>

                {/* Hidden product_id field */}
                <input type="hidden" {...register("product_id", { required: true })} />

                {/* Name Field */}
                <div>
                  <label htmlFor="name" className="label">
                    Name <small className="text-red-700">*</small>
                  </label>
                  <input
                    id="name"
                    type="text"
                    placeholder="Project Name"
                    className={`input ${errors.name ? 'border-red-500' : ''}`}
                    {...register("name", { required: "Name is required" })}
                  />
                  {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name.message}</p>}
                </div>

                {/* Title Field */}
                <div>
                  <label htmlFor="title" className="label">
                    Title <small className="text-red-700">*</small>
                  </label>
                  <input
                    id="title"
                    type="text"
                    placeholder="Project Title"
                    className={`input ${errors.title ? 'border-red-500' : ''}`}
                    {...register("title", {
                      required: "Title is required",
                      onBlur: generateSlug
                    })}
                  />
                  {errors.title && <p className="text-red-600 text-sm mt-1">{errors.title.message}</p>}
                </div>

                {/* Slug Field - Added as requested */}
                <div>
                  <label htmlFor="slug" className="label">
                    Slug <small className="text-red-700">*</small>
                  </label>
                  <input
                    id="slug"
                    type="text"
                    placeholder="project-slug"
                    className={`input ${errors.slug ? 'border-red-500' : ''}`}
                    {...register("slug", { required: "Slug is required" })}
                  />
                  {errors.slug && <p className="text-red-600 text-sm mt-1">{errors.slug.message}</p>}
                </div>

                {/* Client Field */}
                <div>
                  <label htmlFor="client" className="label">
                    Client
                  </label>
                  <input
                    id="client"
                    type="text"
                    placeholder="Client Name"
                    className={`input ${errors.client ? 'border-red-500' : ''}`}
                    {...register("client")}
                  />
                  {errors.client && <p className="text-red-600 text-sm mt-1">{errors.client.message}</p>}
                </div>

                {/* Genre Field */}
                <div>
                  <label htmlFor="genre" className="label">
                    Genre
                  </label>
                  <input
                    id="genre"
                    type="text"
                    placeholder="Genre"
                    className={`input ${errors.genre ? 'border-red-500' : ''}`}
                    {...register("genre")}
                  />
                  {errors.genre && <p className="text-red-600 text-sm mt-1">{errors.genre.message}</p>}
                </div>

                {/* Services Field */}
                <div>
                  <label htmlFor="services" className="label">
                    Services
                  </label>
                  <input
                    id="services"
                    type="text"
                    placeholder="Services Provided"
                    className={`input ${errors.services ? 'border-red-500' : ''}`}
                    {...register("services")}
                  />
                  {errors.services && <p className="text-red-600 text-sm mt-1">{errors.services.message}</p>}
                </div>

                {/* Platform Field */}
                <div>
                  <label htmlFor="platform" className="label">
                    Platform
                  </label>
                  <input
                    id="platform"
                    type="text"
                    placeholder="Platform"
                    className={`input ${errors.platform ? 'border-red-500' : ''}`}
                    {...register("platform")}
                  />
                  {errors.platform && <p className="text-red-600 text-sm mt-1">{errors.platform.message}</p>}
                </div>

                {/* Product Links Section */}
                <div className="border border-gray-300 p-4 rounded-md">
                  <div className="flex justify-between items-center mb-4">
                    <label className="label mb-0">Product Links (Max 3)</label>
                    {fields.length < 3 && (
                      <Button
                        variant="outlined"
                        startIcon={<GrAdd />}
                        size="small"
                        onClick={() => append({ name: "", link: "" })}
                      >
                        Add Link
                      </Button>
                    )}
                  </div>

                  {fields.map((field, index) => (
                    <div key={field.id} className="flex gap-4 items-start mb-4">
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="Label (e.g. Learn More)"
                          className={`input ${errors.product_links?.[index]?.name ? 'border-red-500' : ''}`}
                          {...register(`product_links.${index}.name`, {
                            validate: (value) => {
                              const linkValue = watch(`product_links.${index}.link`);
                              if (linkValue && !value) return "Label is required if link is provided";
                              return true;
                            }
                          })}
                        />
                        {errors.product_links?.[index]?.name && (
                          <p className="text-red-600 text-sm mt-1">{errors.product_links[index].name.message}</p>
                        )}
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="Link URL (e.g. https://example.com)"
                          className={`input ${errors.product_links?.[index]?.link ? 'border-red-500' : ''}`}
                          {...register(`product_links.${index}.link`, {
                            validate: (value) => {
                              const labelValue = watch(`product_links.${index}.name`);
                              if (labelValue && !value) return "Link is required if label is provided";
                              return true;
                            }
                          })}
                        />
                        {errors.product_links?.[index]?.link && (
                          <p className="text-red-600 text-sm mt-1">{errors.product_links[index].link.message}</p>
                        )}
                      </div>
                      <IconButton onClick={() => remove(index)} color="error" className="mt-1">
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  ))}
                </div>

                {/* Overview Field */}
                <div>
                  <label htmlFor="overview" className="label">
                    Overview <small className="text-red-700">*</small>
                  </label>
                  <Editor
                    value={watchedOverview}
                    onChange={handleChangeEditor}
                  />
                  {errors.overview && <p className="text-red-600 text-sm mt-1">{errors.overview.message}</p>}
                </div>

                {/* Video URL Field */}
                <div>
                  <label htmlFor="video" className="label">
                    Video URL
                  </label>
                  <input
                    id="video"
                    type="url"
                    placeholder="Paste Video URL (e.g., YouTube, Vimeo)"
                    className={`input ${errors.video ? 'border-red-500' : ''}`}
                    {...register("video", {
                      pattern: {
                        value: /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/,
                        message: "Please enter a valid URL"
                      }
                    })}
                  />
                  {errors.video && <p className="text-red-600 text-sm mt-1">{errors.video.message}</p>}
                </div>

                {/* Banner Image Section */}
                <div>
                  <div className="my-7 flex items-center justify-between border-b border-gray-400">
                    <div className="pb-3">
                      <h1 className="font-semibold text-2xl">Banner Image</h1>
                      <small className="text-blue-700">
                        Note: Recommended Size: 1920 x 1080 px (JPG, PNG, JPEG)
                        <br />
                        Keep the focal point of the artwork in the top-middle. Avoid putting important details at the very bottom, as they will be covered by the black info bar.
                      </small>
                    </div>
                  </div>
                  <div>
                    {upload.isLoading ? (
                      <Spinner />
                    ) : (
                      <>
                        {imagePreview && (
                          <div className="my-4">
                            <img
                              src={imagePreview}
                              alt="Banner preview"
                              className="object-contain w-48 h-48 border mb-2"
                            />
                            <div className="flex gap-2">
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={handleOpenFileUploadDialog}
                                disabled={upload.isLoading}
                              >
                                Change Image
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={handleDeleteBanner}
                                startIcon={<DeleteIcon />}
                              >
                                Remove Image
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {errors.banner && <p className="text-red-600 text-sm mt-1">{errors.banner.message}</p>}

                    <div className="mt-5">
                      {!imagePreview && (
                        <Button
                          type="button"
                          variant="contained"
                          color="success"
                          className="primaryBtn-contained"
                          onClick={handleOpenFileUploadDialog}
                          disabled={upload.isLoading}
                        >
                          Add Banner Image
                        </Button>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        onChange={handleUploadFile}
                        accept="image/jpeg, image/png, image/jpg"
                      />
                    </div>
                  </div>
                </div>

                {/* Logo Image Section */}
                <div>
                  <div className="my-7 flex items-center justify-between border-b border-gray-400">
                    <div className="pb-3">
                      <h1 className="font-semibold text-2xl">Logo</h1>
                      <small className="text-blue-700">
                        Note: Recommended Size: 500 px width (Transparent PNG)
                      </small>
                    </div>
                  </div>
                  <div>
                    {uploadLogo.isLoading ? (
                      <Spinner />
                    ) : (
                      <>
                        {logoPreview && (
                          <div className="my-4">
                            <img
                              src={logoPreview}
                              alt="Logo preview"
                              className="object-contain w-48 h-48 border mb-2"
                            />
                            <div className="flex gap-2">
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={handleOpenLogoUploadDialog}
                                disabled={uploadLogo.isLoading}
                              >
                                Change Logo
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={handleDeleteLogo}
                                startIcon={<DeleteIcon />}
                              >
                                Remove Logo
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {errors.logo && !logoPreview && <p className="text-red-600 text-sm mt-1">{errors.logo.message}</p>}

                    <div className="mt-5">
                      {!logoPreview && (
                        <Button
                          type="button"
                          variant="contained"
                          color="success"
                          className="primaryBtn-contained"
                          onClick={handleOpenLogoUploadDialog}
                          disabled={uploadLogo.isLoading}
                        >
                          Add Logo
                        </Button>
                      )}
                      <input
                        ref={logoFileInputRef}
                        type="file"
                        className="hidden"
                        onChange={handleUploadLogoFile}
                        accept="image/jpeg, image/png, image/jpg"
                      />
                    </div>
                  </div>
                </div>

                {/* Secondary Image Section - Commented out */}
                {/* <div>
                  <div className="my-7 flex items-center justify-between border-b border-gray-400">
                    <div className="pb-3">
                      <h1 className="font-semibold text-2xl">Secondary Image</h1>
                      <small className="text-red-700">
                        Note: Supported formats: JPG, PNG, JPEG
                      </small>
                    </div>
                  </div>
                  <div>
                    {uploadSecondaryImage.isLoading ? (
                      <Spinner />
                    ) : (
                      <>
                        {secondaryImagePreview && (
                          <img
                            src={secondaryImagePreview}
                            alt="Secondary Image preview"
                            className="object-contain w-48 h-48 my-4 border"
                          />
                        )}
                      </>
                    )}
                    {errors.secondary_img && !secondaryImagePreview && <p className="text-red-600 text-sm mt-1">{errors.secondary_img.message}</p>}
                    
                    <div className="mt-5">
                      <Button
                        type="button"
                        variant="contained"
                        color="success"
                        className="primaryBtn-contained"
                        onClick={handleOpenSecondaryImageUploadDialog}
                        disabled={uploadSecondaryImage.isLoading}
                      >
                        {secondaryImagePreview ? "Update Secondary Image" : "Add Secondary Image"}
                      </Button>
                      <input
                        ref={secondaryImageFileInputRef}
                        type="file"
                        className="hidden"
                        onChange={handleUploadSecondaryImageFile}
                        accept="image/jpeg, image/png, image/jpg"
                      />
                    </div>
                  </div>
                </div> */}

                {/* Thumbnail Section */}
                <div>
                  <div className="my-7 flex items-center justify-between border-b border-gray-400">
                    <div className="pb-3">
                      <h1 className="font-semibold text-2xl">Thumbnail Image</h1>
                      <small className="text-blue-700">
                        Note: Recommended Size: 1200 x 1600 px (JPG, PNG, JPEG)
                      </small>
                    </div>
                  </div>
                  <div>
                    {uploadThumbnail.isLoading ? (
                      <Spinner />
                    ) : (
                      <>
                        {thumbnailPreview && (
                          <div className="my-4">
                            <img
                              src={thumbnailPreview}
                              alt="Thumbnail preview"
                              className="object-contain w-48 h-48 border mb-2"
                            />
                            <div className="flex gap-2">
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={handleOpenThumbnailUploadDialog}
                                disabled={uploadThumbnail.isLoading}
                              >
                                Change Image
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={handleDeleteThumbnail}
                                startIcon={<DeleteIcon />}
                              >
                                Remove Image
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {errors.thumbnail && !thumbnailPreview && <p className="text-red-600 text-sm mt-1">{errors.thumbnail.message}</p>}

                    <div className="mt-5">
                      {!thumbnailPreview && (
                        <Button
                          type="button"
                          variant="contained"
                          color="success"
                          className="primaryBtn-contained"
                          onClick={handleOpenThumbnailUploadDialog}
                          disabled={uploadThumbnail.isLoading}
                        >
                          Add Thumbnail Image
                        </Button>
                      )}
                      <input
                        ref={thumbnailFileInputRef}
                        type="file"
                        className="hidden"
                        onChange={handleUploadThumbnailFile}
                        accept="image/jpeg, image/png, image/jpg"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="text-center my-6">
                <Button
                  type="submit"
                  variant="contained"
                  className="primaryBtn-contained"
                  disabled={upload.isLoading || setDataToServer.isLoading}
                >
                  {setDataToServer.isLoading ? <Spinner /> : "Save Project"}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Error message */}
        {isError && !setDataToServer.isLoading && (
          <Alert
            onClose={closeErrorMessage}
            className="mb-4"
            severity="error"
            sx={{ width: "100%" }}
          >
            Failed to save project. Please check the details and try again.
          </Alert>
        )}
      </div>
    </>
  );
}

export default NewEditProduct;
